import { useCallback, useEffect, useRef, useState } from "react";
import {
  LOG_EXPORT_ENDPOINT,
  LOG_EXPORT_PREVIEW_ENDPOINT,
  LOG_EXPORT_STATUS_ENDPOINT,
} from "../config/urls";
import { useAuth } from "./AuthProvider";

const POLL_INTERVAL_MS = 3000;

// `previewEndpoint` / `exportEndpoint` default to the Logs export API; other
// pages (e.g. Leads, Customers) pass their own. Job status polling always goes
// through the shared logs ExportJob status endpoint.
const useLogExport = (
  filters,
  {
    previewEndpoint = LOG_EXPORT_PREVIEW_ENDPOINT,
    exportEndpoint = LOG_EXPORT_ENDPOINT,
    fallbackFilenamePrefix = "log-export",
  } = {},
) => {
  const { getValidToken } = useAuth();
  const [preview, setPreview] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const previewRequestRef = useRef(0);

  const getHeaders = useCallback(async (extra = {}) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "X-Tenant-Id": tenant,
      ...extra,
    };
  }, [getValidToken]);

  const buildQueryParams = useCallback(() => {
    const cleanEntries = Object.entries(filters || {}).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    );
    return new URLSearchParams(Object.fromEntries(cleanEntries));
  }, [filters]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    // Drop the old preview so export can't run with a stale mode/count while filters change
    const requestId = ++previewRequestRef.current;
    setPreview(null);
    try {
      setError(null);
      const headers = await getHeaders();
      const queryParams = buildQueryParams();
      const response = await fetch(`${previewEndpoint}?${queryParams}`, { headers });
      if (!response.ok) {
        throw new Error("Failed to fetch export preview");
      }
      const data = await response.json();
      if (requestId === previewRequestRef.current) setPreview(data);
    } catch (err) {
      if (requestId !== previewRequestRef.current) return;
      setError(err.message);
      setPreview(null);
    }
  }, [getHeaders, buildQueryParams, previewEndpoint]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  useEffect(() => stopPolling, [stopPolling]);

  const pollJobStatus = useCallback(
    (jobId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const headers = await getHeaders();
          const response = await fetch(`${LOG_EXPORT_STATUS_ENDPOINT}${jobId}/`, { headers });
          if (!response.ok) {
            throw new Error("Failed to fetch export status");
          }
          const data = await response.json();
          setJobStatus(data);

          if (data.status === "ready" || data.status === "failed") {
            stopPolling();
            setLoading(false);
            if (data.status === "ready") {
              setDownloadUrl(data.download_url);
            } else {
              setError(data.error || "Export failed");
            }
          }
        } catch (err) {
          stopPolling();
          setLoading(false);
          setError(err.message);
        }
      }, POLL_INTERVAL_MS);
    },
    [getHeaders, stopPolling],
  );

  const triggerExport = useCallback(async () => {
    if (!preview || preview.export_mode === "blocked") {
      return;
    }

    setLoading(true);
    setError(null);
    setJobStatus(null);
    setDownloadUrl(null);

    try {
      const queryParams = buildQueryParams();
      const headers = await getHeaders();
      const response = await fetch(`${exportEndpoint}?${queryParams}`, {
        method: "POST",
        headers,
      });

      if (preview.export_mode === "sync") {
        if (!response.ok) {
          throw new Error("Failed to export data");
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        const contentDisposition = response.headers.get("content-disposition") || "";
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        a.download = filenameMatch ? filenameMatch[1] : `${fallbackFilenamePrefix}-${timestamp}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setLoading(false);
      } else {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Failed to queue export");
        }
        setJobStatus({ id: data.job_id, status: "queued" });
        pollJobStatus(data.job_id);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message);
    }
  }, [preview, buildQueryParams, getHeaders, pollJobStatus, exportEndpoint, fallbackFilenamePrefix]);

  const reset = useCallback(() => {
    stopPolling();
    setJobStatus(null);
    setDownloadUrl(null);
    setError(null);
    setLoading(false);
  }, [stopPolling]);

  return { preview, jobStatus, downloadUrl, loading, error, triggerExport, reset };
};

export default useLogExport;
